import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Flame, Crown, CheckCircle, Zap, Users
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ELITE_BEAST_CHALLENGES } from "./GrindConstants";

const GrindBeastMode = ({
  isEliteUser,
  weeklyEliteChallenge,
  activeEliteChallenge,
  eliteChallengeProgress,
  onActivateChallenge,
  onCompleteChallenge,
  onCancelChallenge,
  getDifficultyColor,
}) => {
  const navigate = useNavigate();

  return (
    <Card className={`bg-gradient-to-r ${isEliteUser ? 'from-amber-500/20 via-red-500/20 to-orange-500/20 border-amber-500/40' : 'from-zinc-800/50 to-zinc-900/50 border-zinc-700'} relative overflow-hidden`}>
      {!isEliteUser && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="text-center p-4">
            <Crown className="w-10 h-10 mx-auto text-amber-500 mb-2" />
            <p className="text-white font-bold mb-1">Elite Beast Mode</p>
            <p className="text-sm text-zinc-400 mb-3">Unlock extreme challenges</p>
            <Button 
              size="sm" 
              className="bg-amber-600 hover:bg-amber-500"
              onClick={() => navigate('/subscription')}
              data-testid="unlock-elite-challenges-btn"
            >
              Upgrade to Elite
            </Button>
          </div>
        </div>
      )}
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-red-600 flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white flex items-center gap-2">
                Elite Beast Mode
                <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">EXCLUSIVE</Badge>
              </h3>
              <p className="text-xs text-muted-foreground">Extreme challenges for elite athletes</p>
            </div>
          </div>
          {isEliteUser && weeklyEliteChallenge && (
            <Badge className="bg-red-500/20 text-red-400 animate-pulse">Weekly Challenge</Badge>
          )}
        </div>

        {/* Active Elite Challenge Banner */}
        {activeEliteChallenge && (
          <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-red-600/30 to-orange-600/30 border border-red-500/50 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{activeEliteChallenge.icon}</span>
                <div>
                  <p className="font-bold text-white">{activeEliteChallenge.name} ACTIVE</p>
                  <p className="text-sm text-red-300">{activeEliteChallenge.description}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-green-600 hover:bg-green-500" onClick={() => onCompleteChallenge(activeEliteChallenge.id)}>
                  <CheckCircle className="w-4 h-4 mr-1" /> Complete
                </Button>
                <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/20" onClick={onCancelChallenge}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Weekly Featured Challenge */}
        {weeklyEliteChallenge && !activeEliteChallenge && (
          <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-amber-600/20 to-red-600/20 border border-amber-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{weeklyEliteChallenge.icon}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-white">{weeklyEliteChallenge.name}</p>
                    <Badge className={getDifficultyColor(weeklyEliteChallenge.difficulty)}>
                      {weeklyEliteChallenge.difficulty}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{weeklyEliteChallenge.description}</p>
                  <p className="text-xs text-amber-400 mt-1">+{weeklyEliteChallenge.points} points</p>
                </div>
              </div>
              <Button
                className="bg-gradient-to-r from-amber-600 to-red-600 hover:from-amber-500 hover:to-red-500"
                onClick={() => onActivateChallenge(weeklyEliteChallenge)}
                disabled={eliteChallengeProgress[weeklyEliteChallenge.id]?.completed}
                data-testid="start-weekly-elite-challenge"
              >
                {eliteChallengeProgress[weeklyEliteChallenge.id]?.completed ? (
                  <><CheckCircle className="w-4 h-4 mr-2" /> Completed</>
                ) : (
                  <><Flame className="w-4 h-4 mr-2" /> Accept</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* All Elite Challenges Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {ELITE_BEAST_CHALLENGES.slice(0, 5).map((challenge) => {
            const isCompleted = eliteChallengeProgress[challenge.id]?.completed;
            const isWeekly = weeklyEliteChallenge?.id === challenge.id;
            return (
              <div 
                key={challenge.id}
                className={`p-2 rounded-lg border text-center cursor-pointer transition-all hover:scale-105 ${
                  isCompleted 
                    ? 'bg-green-500/20 border-green-500/30' 
                    : isWeekly
                      ? 'bg-amber-500/10 border-amber-500/30 ring-1 ring-amber-500/50'
                      : 'bg-zinc-800/50 border-zinc-700 hover:border-amber-500/50'
                }`}
                onClick={() => !isCompleted && !activeEliteChallenge && onActivateChallenge(challenge)}
                title={challenge.description}
              >
                <span className="text-2xl">{challenge.icon}</span>
                <p className="text-xs font-medium text-white mt-1 truncate">{challenge.name}</p>
                <p className="text-[10px] text-amber-400">+{challenge.points}</p>
                {isCompleted && <CheckCircle className="w-3 h-3 mx-auto text-green-500 mt-1" />}
              </div>
            );
          })}
        </div>
        
        <div className="mt-3 text-center">
          <Button variant="ghost" size="sm" className="text-amber-400 hover:text-amber-300" onClick={() => toast.info('More challenges coming soon!')}>
            View All {ELITE_BEAST_CHALLENGES.length} Challenges →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default GrindBeastMode;
